import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Sprint 21 — Financial timeline (year/month) shared context.
 *
 * Loads the tenant's MonthlyPeriod list on login and exposes:
 *   • periodKey       : currently selected "YYYY-MM"
 *   • currentPeriodKey: today's live month
 *   • periods         : full list, newest first (metadata + status)
 *   • setPeriodKey(k) : switch UI to a different month
 *   • closePeriod / reopenPeriod
 *   • isSelectedClosed: true when the active period is read-only
 *   • refresh()       : reload from API
 */
const PeriodContext = createContext(null);

export function PeriodProvider({ children }) {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [currentPeriodKey, setCurrentPeriodKey] = useState(null);
  const [periodKey, setPeriodKeyState] = useState(() => {
    try { return localStorage.getItem("gp_period_key") || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const { data } = await api.get("/periods");
      setPeriods(data?.items || []);
      setCurrentPeriodKey(data?.current || null);
      setPeriodKeyState((prev) => {
        const stillValid = prev && (data?.items || []).some((p) => p.period_key === prev);
        return stillValid ? prev : (data?.current || null);
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const setPeriodKey = useCallback((k) => {
    setPeriodKeyState(k);
    try { if (k) localStorage.setItem("gp_period_key", k); } catch {}
  }, []);

  const closePeriod = useCallback(async (k) => {
    await api.post("/periods/close", { period_key: k });
    await load();
  }, [load]);

  const reopenPeriod = useCallback(async (k) => {
    await api.post("/periods/reopen", { period_key: k });
    await load();
  }, [load]);

  const selectedPeriod = useMemo(
    () => (periods || []).find((p) => p.period_key === periodKey) || null,
    [periods, periodKey]
  );

  const isSelectedClosed = selectedPeriod?.status === "closed";
  const isSelectedLive = periodKey === currentPeriodKey && selectedPeriod?.status !== "closed";

  return (
    <PeriodContext.Provider
      value={{
        periods,
        periodKey,
        currentPeriodKey,
        selectedPeriod,
        isSelectedClosed,
        isSelectedLive,
        loading,
        setPeriodKey,
        closePeriod,
        reopenPeriod,
        refresh: load,
      }}
    >
      {children}
    </PeriodContext.Provider>
  );
}

export function usePeriod() {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error("usePeriod must be used inside PeriodProvider");
  return ctx;
}
